import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Generate a URL-safe slug from a string.
 * Pure function, no side effects.
 *
 * @param {string} title - The event title
 * @param {number} [year] - Year to append if not present in title
 * @returns {string} URL-safe slug, max 80 chars
 */
export function slugify(title, year) {
  let slug = title
    .normalize('NFD')                        // decompose accented chars
    .replace(/[\u0300-\u036f]/g, '')         // strip diacritical marks
    .replace(/[\u{1F600}-\u{1FFFF}]/gu, '') // strip emoji
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')           // remove non-alphanumeric (keep spaces/hyphens)
    .replace(/[\s-]+/g, '-')                 // collapse whitespace + hyphens
    .replace(/^-+|-+$/g, '');               // trim leading/trailing hyphens

  // Append year if not already in the slug
  if (year && !slug.includes(String(year))) {
    slug = slug ? `${slug}-${year}` : `${year}`;
  }

  // Truncate to max 80 chars
  const maxLen = 80;
  if (slug.length > maxLen) {
    slug = slug.substring(0, maxLen).replace(/-+$/, '');
  }

  return slug;
}

/**
 * Generate a random 4-character alphanumeric suffix.
 * Uses characters safe for URLs: a-z, 0-9
 */
function randomSuffix() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const array = new Uint8Array(4);
  crypto.getRandomValues(array);
  for (const byte of array) {
    result += chars[byte % chars.length];
  }
  return result;
}

/**
 * Check if a slug already exists in the shared_events collection.
 *
 * @param {string} slug
 * @returns {Promise<boolean>}
 */
async function slugExists(slug) {
  const q = query(
    collection(db, 'shared_events'),
    where('slug', '==', slug)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Generate a unique slug with Firestore collision check.
 * Retries up to 3 times with new suffixes on collision.
 *
 * @param {string} title - The event title
 * @param {number} [year] - Year to append if not in title (defaults to current year)
 * @returns {Promise<string>} Unique slug
 * @throws {Error} If unable to generate unique slug after retries
 */
export async function generateUniqueSlug(title, year) {
  const effectiveYear = year || new Date().getFullYear();
  const base = slugify(title, effectiveYear);
  const maxRetries = 3;

  // First try the base slug without a suffix
  if (base) {
    const exists = await slugExists(base);
    if (!exists) return base;
  }

  const baseWithRoom = base.length > 75
    ? base.substring(0, 75).replace(/-+$/, '')
    : base;

  for (let i = 0; i <= maxRetries; i++) {
    const candidate = `${baseWithRoom}-${randomSuffix()}`;
    const exists = await slugExists(candidate);
    if (!exists) {
      return candidate;
    }
  }

  throw new Error(`Unable to generate unique slug for "${title}" after ${maxRetries + 1} attempts`);
}
